import { isDefined } from "@clipboard-health/util-ts";
import { type ClipboardScoreLog } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreHistory";
import { type ClipboardScorePolicy } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { ClipboardScoreActions } from "@src/appV2/redesign/ClipboardScore/utils/constants";

import { type GradientVariant, type ScoreChange } from "../types";
import { buildScoreChanges } from "./buildScoreChanges";
import { calculatePointsSavedWithLatenessCredit } from "./calculatePointsSavedWithLatenessCredit";
import { getGradientVariant } from "./getGradientVariant";
import { getScoreSubtitle } from "./getScoreSubtitle";

interface ScoreDataForEmergencyCreditsClockOutFlowParams {
  scoreLogs: ClipboardScoreLog[];
  currentScore: number;
  scoreChange: number;
  latenessMinutes: number;
  isSecondShiftInDouble: boolean;
  isFirstShiftInDouble: boolean;
  wasBookedWithShortLeadTime: boolean;
  newlyRestricted: boolean;
  isArrivalTimeFeatureEnabled: boolean;
  formattedTargetArrivalTime?: string;
  shortLeadTimeThresholdInHours: number;
  policy?: ClipboardScorePolicy;
  hasAvailableLatenessCredit: boolean;
}

export interface EmergencyClockOutFlowShiftData {
  startScore: number;
  scoreChanges: ScoreChange[];
  subtitle: string;
  /**
   * Note displayed below the subtitle when this shift is the first in a double.
   * Rendered as a separate, smaller text element.
   */
  doubleShiftNote?: string;
  gradientVariant: GradientVariant;
  penaltyStartMinutes?: number;
  /**
   * Points saved by applying a lateness credit (positive number).
   * Undefined when the worker has no lateness credit or no matching config.
   */
  savedPoints?: number;
}

/**
 * Composes buildScoreChanges, getScoreSubtitle, and getGradientVariant
 * into a single data object for the Emergency Credits clock-out flow.
 *
 * When the worker has an available lateness credit, computes the full penalty
 * from policy config so the START_SHIFT_LATE line item shows the unreduced
 * penalty, and returns the savedPoints for the credit line item.
 */
export function buildScoreDataForEmergencyCreditsClockOutFlow(
  params: ScoreDataForEmergencyCreditsClockOutFlowParams
): EmergencyClockOutFlowShiftData {
  const {
    scoreLogs,
    currentScore,
    scoreChange,
    latenessMinutes,
    isSecondShiftInDouble,
    isFirstShiftInDouble,
    wasBookedWithShortLeadTime,
    newlyRestricted,
    isArrivalTimeFeatureEnabled,
    formattedTargetArrivalTime,
    shortLeadTimeThresholdInHours,
    policy,
    hasAvailableLatenessCredit,
  } = params;

  const latenessConfig = policy?.latenessConfig;

  const penaltyStartMinutes =
    isDefined(latenessConfig) && latenessConfig.length > 0
      ? Math.min(...latenessConfig.map((config) => config.minutesLateRange.startFrom))
      : undefined;

  const latenessCreditResult = hasAvailableLatenessCredit
    ? calculatePointsSavedWithLatenessCredit({ latenessMinutes, latenessConfig })
    : undefined;

  const scoreChanges = buildScoreChanges({
    scoreLogs,
    latenessMinutes,
    latenessConfig,
    fullLatenessPenalty: latenessCreditResult?.fullLatenessPenalty,
  });

  const startScore = currentScore - scoreChange;

  const wasLate = isDefined(latenessMinutes) && latenessMinutes > 0;
  const didLosePointsForTimeliness = scoreLogs.some(
    (log) =>
      log.action === ClipboardScoreActions.START_SHIFT_LATE &&
      (log.clipboardScoreAdjustment ?? 0) < 0
  );

  const subtitle = getScoreSubtitle({
    wasLate,
    latenessMinutes,
    didLosePointsForTimeliness,
    isArrivalTimeFeatureEnabled,
    isSecondShiftInDouble,
    wasBookedWithShortLeadTime,
    shortLeadTimeThresholdInHours,
    formattedTargetArrivalTime,
  });

  const doubleShiftNote = isFirstShiftInDouble
    ? "This is a double, so your next shift will not receive arrival time bonuses or penalties."
    : undefined;

  const gradientVariant = getGradientVariant({
    scoreChange,
    currentScore,
    latenessMinutes,
    newlyRestricted,
    isSecondShiftInDouble,
    wasBookedWithShortLeadTime,
    didLosePointsForTimeliness,
    penaltyStartMinutes,
  });

  return {
    startScore,
    scoreChanges,
    subtitle,
    doubleShiftNote,
    gradientVariant,
    penaltyStartMinutes,
    savedPoints: latenessCreditResult?.savedPoints,
  };
}
